import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ClientActions = ({ onApprove, onReject, isExpired }) => {
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleApprove = async () => {
    setIsSubmitting(true);
    try {
      await onApprove();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!feedback?.trim()) {
      setShowFeedback(true);
      return;
    }
    
    setIsSubmitting(true);
    try {
      await onReject(feedback);
      setFeedback('');
      setShowFeedback(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRejectClick = () => {
    setShowFeedback(true);
  };

  if (isExpired) {
    return (
      <div className="bg-card rounded-lg border border-border p-6 elevation-1">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-error/10 rounded-full flex items-center justify-center mx-auto">
            <Icon name="AlertTriangle" size={32} color="var(--color-error)" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-foreground">Preview Expired</h3>
            <p className="text-sm text-muted-foreground mt-1">
              This design preview link has expired. Please contact your jeweler for a new preview link.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border p-6 elevation-1 space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-heading text-foreground mb-2">
          What do you think of this design?
        </h3>
        <p className="text-sm text-muted-foreground">
          Your feedback helps us create the perfect piece for you
        </p>
      </div>
      {!showFeedback ? (
        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            variant="success"
            size="lg"
            onClick={handleApprove}
            loading={isSubmitting}
            iconName="Check"
            iconPosition="left"
            className="flex-1"
          >
            Approve Design
          </Button>
          <Button
            variant="outline"
            size="lg"
            onClick={handleRejectClick}
            iconName="MessageSquare"
            iconPosition="left"
            className="flex-1"
          >
            Request Changes
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <Input
            label="What changes would you like to see?"
            type="text"
            placeholder="Please describe the changes you'd like to make to this design..."
            value={feedback}
            onChange={(e) => setFeedback(e?.target?.value)}
            description="Your detailed feedback helps us understand your vision better"
            required
            className="min-h-[120px]"
          />
          
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="warning"
              size="lg"
              onClick={handleReject}
              loading={isSubmitting}
              disabled={!feedback?.trim()}
              iconName="Send"
              iconPosition="left"
              className="flex-1"
            >
              Send Feedback
            </Button>
            <Button
              variant="ghost"
              size="lg"
              onClick={() => {
                setShowFeedback(false);
                setFeedback('');
              }}
              className="sm:w-auto"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
      <div className="pt-4 border-t border-border">
        <div className="flex items-center justify-center space-x-2 text-xs text-muted-foreground">
          <Icon name="Shield" size={12} />
          <span>Your response is securely transmitted</span>
          <Icon name="Lock" size={12} />
        </div>
      </div>
    </div>
  );
};

export default ClientActions;